import { useState, useEffect } from 'react';
import { Routes, Route, NavLink } from 'react-router-dom';
import { LogOut } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../lib/supabase';
import type { Band, BandManager } from '../../types/database';
import { BandSelector } from './BandSelector';
import { BandEditor } from './BandEditor';
import { Analytics } from './Analytics';
import styles from './AdminDashboard.module.css';

export function AdminDashboard() {
  const { user, signOut } = useAuth();
  const [managedBands, setManagedBands] = useState<(BandManager & { band: Band })[]>([]);
  const [selectedBandId, setSelectedBandId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchManagedBands = async () => {
    if (!user) return;

    setLoading(true);
    const { data, error } = await supabase
      .from('band_managers')
      .select('*, band:bands(*)')
      .eq('user_id', user.id);

    if (!error && data) {
      const validData = data.filter((d) => d.band !== null) as (BandManager & { band: Band })[];
      setManagedBands(validData);
      if (validData.length > 0) {
        setSelectedBandId(validData[0].band_id);
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchManagedBands();
  }, [user]);

  const selectedBand = managedBands.find((m) => m.band_id === selectedBandId)?.band;

  if (loading) {
    return (
      <div className={styles.loading}>
        <div className={styles.spinner} />
      </div>
    );
  }

  // If no managed bands, show selector to link to a band
  if (managedBands.length === 0) {
    return <BandSelector onBandLinked={fetchManagedBands} />;
  }

  return (
    <div className={styles.container}>
      {/* Sidebar */}
      <aside className={styles.sidebar}>
        <div className={styles.logo}>
          <img src="/logo-small.png" alt="Mas Match" className={styles.logoImage} />
          <h1>Band Manager</h1>
        </div>

        {/* Band selector */}
        {managedBands.length > 1 && (
          <select
            className={styles.bandSelect}
            value={selectedBandId || ''}
            onChange={(e) => setSelectedBandId(e.target.value)}
          >
            {managedBands.map((m) => (
              <option key={m.band_id} value={m.band_id}>
                {m.band.name}
              </option>
            ))}
          </select>
        )}

        {/* Navigation */}
        <nav className={styles.nav}>
          <NavLink
            to="/admin"
            end
            className={({ isActive }) => (isActive ? styles.active : '')}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
            Dashboard
          </NavLink>
          <NavLink
            to="/admin/edit"
            className={({ isActive }) => (isActive ? styles.active : '')}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            Edit Band
          </NavLink>
        </nav>

        <button className={styles.backBtn} onClick={signOut}>
          <LogOut size={16} />
          Sign Out
        </button>
      </aside>

      {/* Main content */}
      <main className={styles.main}>
        {selectedBand ? (
          <Routes>
            <Route index element={<Analytics band={selectedBand} />} />
            <Route path="edit" element={<BandEditor band={selectedBand} />} />
          </Routes>
        ) : (
          <div className={styles.loading}>
            <p>Loading band data...</p>
          </div>
        )}
      </main>
    </div>
  );
}
